import React, { useEffect, useState } from 'react';
import useRootSelector from 'common/hooks/useRootSelector';
import { FormattedMessage } from 'react-intl';
import { Grid, MenuItem } from '@material-ui/core';
import { Controller, useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { PermissionsGroupsLabels } from 'modules/permissions/constants';
import { AppPermissionLevel } from 'common/constants/permissions';
import { IPolicyFormData, IPolicyListItem } from 'modules/permissions/interfaces/permissions';
import messages from '../../messages/messages';
import commonMessages from 'common/messages/messages';
import inputLabels from 'common/messages/inputLabels';
import inputErrors from 'common/messages/inputErrors';
import { selectCurrentUserSelectedLevel } from 'modules/authentication/state/selectors';
import { DialogComponent, Select, TextField } from 'common/components';
import CollapsedComponent from './CollapsedComponent';
import { PolicyLevelSwitcher } from '..';
import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';
import isFormDirty from 'common/hooks/isFormDirty';
import { getRequiredErrorMessage } from 'common/utils/validation';
import { useGetPolicyItem } from 'api/policy.api';
import {
  getAppPermissionLevelByGroup,
  getAppPermissionLevelGroupOptions,
  getAppPermissionLevelGroups,
} from 'utils/permission.utils';

interface EditPolicyModalProps {
  selectedRow: IPolicyListItem | null;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (data: IPolicyFormData) => void;
}

const validationSchema = yup.object().shape({
  title: yup
    .string()
    .trim()
    .required(getRequiredErrorMessage),
  permissionGroup: yup
    .mixed()
    .notOneOf(['', null], getRequiredErrorMessage)
    .required(getRequiredErrorMessage),
  permissionSet: yup
    .array()
    .nullable()
    .min(1, () => inputErrors.permissionSetLengthError),
});

const defaultValues = { title: '', permissionGroup: '', permissionSet: [] };

export default function EditPolicyModal({
  selectedRow,
  onClose,
  onSubmit,
  isLoading,
}: EditPolicyModalProps): JSX.Element {
  const currentPLevel = useRootSelector(selectCurrentUserSelectedLevel);
  const renderIntlMessage = useRenderIntlMessage();

  const initialLevel = Object.values(AppPermissionLevel).includes(currentPLevel)
    ? currentPLevel
    : AppPermissionLevel.Corporate;

  const [selectedLevel, setSelectedLevel] = useState<AppPermissionLevel>(initialLevel);

  const { data: policyItem } = useGetPolicyItem(selectedRow?.id || '', {
    skip: !selectedRow?.id,
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    formState,
  } = useForm<IPolicyFormData>({
    defaultValues,
    resolver: yupResolver(validationSchema) as any, // TODO - PRM-1810 need resolver type
    mode: 'onBlur',
  });

  const isDirty = isFormDirty(formState);
  const selectedPermissionsGroup: string = useWatch({ control, name: 'permissionGroup' });

  const onSubmitHandle = data => {
    onSubmit(data);
  };

  const handlePolicyLevelChange = (level: AppPermissionLevel) => {
    setSelectedLevel(level);
    setValue('permissionSet', []);
    setValue('permissionGroup', '');
  };

  useEffect(() => {
    if (policyItem?.id) {
      const appPermissionLevel = getAppPermissionLevelByGroup(policyItem.permissionGroup);
      reset(policyItem);

      if (appPermissionLevel) {
        setSelectedLevel(appPermissionLevel);
      }
    }
  }, [reset, policyItem]);

  const appPermissionLevelGroups = getAppPermissionLevelGroups(selectedLevel);
  const appPermissionLevelGroupOptions = getAppPermissionLevelGroupOptions(selectedLevel);

  return (
    <DialogComponent
      title={
        selectedRow ? (
          <FormattedMessage
            {...messages.editPolicyItemTitle}
            values={{ title: selectedRow.title }}
          />
        ) : (
          <FormattedMessage {...messages.newPolicyItemTitle} />
        )
      }
      submitBtnTitle={<FormattedMessage {...commonMessages.saveBtn} />}
      size="xs"
      isOpen
      onClose={onClose}
      onSubmit={handleSubmit(onSubmitHandle)}
      disableFullScreenBehavior
      loading={isLoading}
      isDirty={isDirty}
      keepMounted
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            name="title"
            control={control}
            defaultValue={defaultValues.title}
            render={({ field }) => (
              <TextField
                fullWidth
                variant="outlined"
                autoComplete="none"
                onChange={field.onChange}
                onBlur={field.onBlur}
                label={<FormattedMessage {...inputLabels.name} />}
                value={field.value}
                error={!!errors.title}
                helperText={renderIntlMessage(errors?.title?.message)}
              />
            )}
          />
        </Grid>

        {selectedLevel !== AppPermissionLevel.Peak && (
          <Grid item xs={12}>
            <PolicyLevelSwitcher
              isDisabled={!!selectedRow}
              onChange={handlePolicyLevelChange}
              value={selectedLevel}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Controller
            name="permissionGroup"
            control={control}
            render={({ field }) => (
              <Select
                fullWidth
                label={<FormattedMessage {...inputLabels.policyType} />}
                renderValue={(v: string) => PermissionsGroupsLabels[v]}
                variant="outlined"
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={!!errors.permissionGroup}
                helperText={renderIntlMessage(errors?.permissionGroup?.message)}
                disabled={!!selectedRow}
                menuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: '300px',
                    },
                  },
                }}
              >
                {appPermissionLevelGroups.map(group => (
                  <MenuItem key={group} value={group}>
                    {PermissionsGroupsLabels[group]}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </Grid>

        <Grid item xs={12}>
          {appPermissionLevelGroupOptions && selectedPermissionsGroup ? (
            <Controller
              name="permissionSet"
              control={control}
              render={({ field }) => (
                <CollapsedComponent
                  isCheckable
                  permissions={appPermissionLevelGroupOptions[selectedPermissionsGroup]}
                  permissionSet={field.value}
                  onChange={field.onChange}
                  error={!!errors.permissionSet}
                  helperText={renderIntlMessage((errors?.permissionSet as any)?.message)}
                />
              )}
            />
          ) : null}
        </Grid>
      </Grid>
    </DialogComponent>
  );
}
